/**
 * The app's side of the Worker's JSON routes.
 *
 * One request helper, one error shape, and a typed call per route the
 * signed-in screens read or write. Every call resolves to an `ApiResult`
 * and never throws: each route has designed failures the screen must
 * render (a 401 that sends the reader to /login, a 503 that says the
 * deployment is not configured, a 429 that says try again later), and a
 * thrown error is how a designed state becomes a blank card.
 *
 * `org_id` appears nowhere in these calls. The Worker reads it from the
 * session cookie the browser carries, and that is the whole isolation
 * model (src/worker/router.ts). A call here cannot name another tenant.
 */

import type {
  CohortKind,
  CohortPayload,
  CohortRecord,
  CohortsPayload,
  MatchRequestWire,
  MatchStatusWire,
  MatchesPayload,
  MetricsPayload,
  RosterPayload,
  SessionPayload,
} from "~/worker/lib/api-types";
import type { CapabilityManifest } from "~/worker/lib/capabilities";
import type { Cell } from "~/lib/suppression";

export type {
  CohortKind,
  CohortPayload,
  CohortRecord,
  CohortsPayload,
  MatchRequestWire,
  MatchStatusWire,
  MatchesPayload,
  MetricsPayload,
  RosterPayload,
  SessionPayload,
};

/** The Worker's one error shape. `message` is safe to show verbatim. */
export interface ApiError {
  readonly code: string;
  readonly message: string;
  /** Seconds until the same call would be allowed, when the Worker refused
   *  purely on time. Absent on every other refusal. */
  readonly retryAfter?: number;
}

export type ApiResult<T = void> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly status: number | null; readonly error: ApiError };

const TIMEOUT_MS = 12_000;

export const NETWORK_ERROR: ApiError = {
  code: "NETWORK",
  message: "We could not reach P3. Check your connection and try again.",
};

const UNEXPECTED_ERROR: ApiError = {
  code: "UNEXPECTED",
  message: "Something went wrong at P3. Try again, and tell us if it persists.",
};

export interface RawResponse {
  readonly status: number;
  readonly json: unknown;
}

export async function request(
  path: string,
  init: { method?: "GET" | "POST" | "PATCH" | "DELETE"; body?: unknown } = {},
): Promise<RawResponse | null> {
  if (typeof fetch === "undefined") return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const headers: Record<string, string> = { accept: "application/json" };
    if (init.body !== undefined) headers["content-type"] = "application/json";
    const response = await fetch(path, {
      method: init.method ?? "GET",
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      credentials: "same-origin",
      signal: controller.signal,
    });
    let json: unknown = null;
    if (response.status !== 204) {
      try {
        json = await response.json();
      } catch {
        json = null;
      }
    }
    return { status: response.status, json };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** The Worker's `{ error: { code, message } }`, or a generic one. */
export function errorOf(json: unknown): ApiError {
  if (json && typeof json === "object" && "error" in json) {
    const error = (json as { error?: unknown }).error;
    if (error && typeof error === "object") {
      const code = (error as { code?: unknown }).code;
      const message = (error as { message?: unknown }).message;
      if (typeof code === "string" && typeof message === "string" && message.trim() !== "") {
        const retryAfter = (error as { retryAfter?: unknown }).retryAfter;
        return typeof retryAfter === "number" && Number.isFinite(retryAfter) && retryAfter > 0
          ? { code, message, retryAfter }
          : { code, message };
      }
    }
  }
  return UNEXPECTED_ERROR;
}

function failure<T>(raw: RawResponse | null): ApiResult<T> {
  if (raw === null) return { ok: false, status: null, error: NETWORK_ERROR };
  return { ok: false, status: raw.status, error: errorOf(raw.json) };
}

/** A GET whose 200 body is the value. Anything else is the failure it says. */
async function get<T>(path: string, accept: (json: unknown) => json is T): Promise<ApiResult<T>> {
  const raw = await request(path);
  if (raw !== null && raw.status === 200 && accept(raw.json)) return { ok: true, value: raw.json };
  return failure<T>(raw);
}

const isObject = (json: unknown): json is Record<string, unknown> =>
  json !== null && typeof json === "object" && !Array.isArray(json);

const isStringList = (value: unknown): value is readonly string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

function isCapabilityManifest(json: unknown): json is CapabilityManifest {
  if (!isObject(json) || !isStringList(json["shipped"]) || !isStringList(json["notOffered"])) return false;
  const planned = json["plannedFrom"];
  return (
    Array.isArray(planned) &&
    planned.every((p) => isObject(p) && typeof p["item"] === "string" && (p["date"] === null || typeof p["date"] === "string")) &&
    isObject(json["suppression"])
  );
}

/**
 * `GET /api/enterprise/capabilities`, a public route: what P3 does, what it
 * plans and what it does not offer, from the Worker's one manifest. A
 * signed-in screen already holds it on the session; the sample dashboard,
 * which has no session, reads it here.
 */
export async function getCapabilities(): Promise<ApiResult<CapabilityManifest>> {
  return get("/api/enterprise/capabilities", isCapabilityManifest);
}

/* ==========================================================================
   The institution's own record
   ========================================================================== */

/** `GET /api/enterprise/organization`: the row, in the Worker's column names. */
export interface OrganizationPayload {
  readonly legal_name: string;
  readonly display_name: string;
  readonly slug: string;
  readonly website: string | null;
  readonly address_line1: string | null;
  readonly address_line2: string | null;
  readonly address_city: string | null;
  readonly address_state: string | null;
  readonly address_postal: string | null;
  readonly address_country: string | null;
  readonly logo_asset_url: string | null;
  readonly enrollment_estimate: number | null;
  readonly fiscal_year_end: string | null;
  readonly capacity_band: string | null;
  readonly status: string;
}

const isOrganization = (json: unknown): json is OrganizationPayload =>
  isObject(json) && typeof json["legal_name"] === "string" && typeof json["slug"] === "string";

export const getOrganization = () => get("/api/enterprise/organization", isOrganization);

/* ==========================================================================
   Billing
   ========================================================================== */

export interface BillingPlan {
  readonly band: string;
  readonly bandLabel: string | null;
  readonly bandLimit: number | null;
  readonly amountCents: number;
  readonly interval: "month" | "year";
  readonly collectionMethod: string;
  readonly periodStart: number | null;
  readonly renewsAt: number | null;
  readonly accessUntil: number;
  readonly accessMode: string;
  readonly cancelAtPeriodEnd: boolean;
  /** When renewal is off: the day the subscription ends (a monthly plan bills one more full month first). */
  readonly endsAt?: number | null;
}

export interface BillingPayload {
  readonly plan: BillingPlan | null;
  readonly capacity: { readonly claimed: Cell<number>; readonly bandNow: string; readonly note: string };
  readonly invoices: {
    readonly state: "ok" | "unavailable";
    readonly reason?: string;
    readonly rows: readonly unknown[];
  };
  readonly documents: {
    readonly w9: string;
    readonly terms: string;
    readonly dataSharingAddendum: string;
    readonly subprocessors: string;
  };
}

const isBilling = (json: unknown): json is BillingPayload =>
  isObject(json) && "plan" in json && isObject(json["capacity"]) && isObject(json["invoices"]);

export const getBilling = () => get("/api/enterprise/billing", isBilling);

export interface RenewalChange {
  readonly renew: boolean;
  readonly changed: boolean;
  readonly renewsAt: number | null;
  readonly endsAt?: number | null;
  readonly notified?: boolean;
}

export async function patchBilling(renew: boolean): Promise<ApiResult<RenewalChange>> {
  const raw = await request("/api/enterprise/billing", { method: "PATCH", body: { renew } });
  if (raw !== null && raw.status === 200 && isObject(raw.json) && typeof raw.json["renew"] === "boolean") {
    return { ok: true, value: raw.json as unknown as RenewalChange };
  }
  return failure<RenewalChange>(raw);
}

/* ==========================================================================
   The cohort and the figures
   ========================================================================== */

const isCohort = (json: unknown): json is CohortPayload =>
  isObject(json) && isObject(json["claimed"]) && Array.isArray(json["joinCodes"]);

export const getCohort = () => get("/api/enterprise/cohort", isCohort);

const isMetrics = (json: unknown): json is MetricsPayload =>
  isObject(json) && typeof json["capturedAt"] === "number" && isObject(json["sections"]);

/**
 * `GET /api/enterprise/metrics`. A 503 SNAPSHOT_PENDING is a real state
 * with a designed screen (the day-one claim campaign), returned here as
 * `value: null` rather than as a failure, so the screen can tell "no figures
 * yet" from "could not reach P3".
 */
/**
 * The institution's document, or one cut of it: a cohort of its own, or one
 * of the two slices (Sep 17 2026), never both. A cut the tick has not written
 * yet arrives as `null`, which is a state the page draws rather than an error.
 */
export async function getMetrics(cut?: { cohort?: string; slice?: "matched" | "waiting" }): Promise<ApiResult<MetricsPayload | null>> {
  const query = cut?.cohort
    ? `?cohort=${encodeURIComponent(cut.cohort)}`
    : cut?.slice
      ? `?slice=${cut.slice}`
      : "";
  const raw = await request(`/api/enterprise/metrics${query}`);
  if (raw !== null && raw.status === 200 && isMetrics(raw.json)) return { ok: true, value: raw.json };
  if (raw !== null && raw.status === 503 && errorOf(raw.json).code === "SNAPSHOT_PENDING") {
    return { ok: true, value: null };
  }
  return failure<MetricsPayload | null>(raw);
}

/** Who has joined (Sep 17 2026). 403 for a viewer; the page says so. */
export async function getRoster(): Promise<ApiResult<RosterPayload>> {
  const raw = await request("/api/enterprise/roster");
  if (raw !== null && raw.status === 200 && isRoster(raw.json)) return { ok: true, value: raw.json };
  return failure<RosterPayload>(raw);
}

function isRoster(json: unknown): json is RosterPayload {
  if (!json || typeof json !== "object") return false;
  const r = json as Record<string, unknown>;
  return typeof r["available"] === "boolean" && Array.isArray(r["students"]) && Array.isArray(r["mentors"]);
}

/* ==========================================================================
   Matching (Sep 17 2026): a mentor for a student, asked from the portal.
   Admin and owner only; ids from the roster, never names.
   ========================================================================== */

const isMatches = (json: unknown): json is MatchesPayload =>
  isObject(json) && typeof json["available"] === "boolean" && Array.isArray(json["requests"]);

export const getMatches = () => get("/api/enterprise/matches", isMatches);

const isMatchRequest = (json: unknown): json is { request: MatchRequestWire } =>
  isObject(json) && isObject(json["request"]) && typeof json["request"]["id"] === "string";

/** `POST /api/enterprise/match`: 201 with the recorded request; the Worker's or the gateway's refusal in its own words. */
export async function requestMatch(studentId: string, mentorId: string): Promise<ApiResult<MatchRequestWire>> {
  const raw = await request("/api/enterprise/match", { method: "POST", body: { studentId, mentorId } });
  if (raw !== null && raw.status === 201 && isMatchRequest(raw.json)) return { ok: true, value: raw.json.request };
  return failure<MatchRequestWire>(raw);
}

/** `POST /api/enterprise/match/:id/withdraw` (Sep 18 2026): 200 with the request now INACTIVE and dated withdrawn; the Worker's or the gateway's refusal in its own words. */
export async function withdrawMatch(requestId: string): Promise<ApiResult<MatchRequestWire>> {
  const raw = await request(`/api/enterprise/match/${encodeURIComponent(requestId)}/withdraw`, { method: "POST" });
  if (raw !== null && raw.status === 200 && isMatchRequest(raw.json)) return { ok: true, value: raw.json.request };
  return failure<MatchRequestWire>(raw);
}

/** `POST /api/enterprise/match/suggest`: the platform's suggested mentors for a student, ids on the roster. */
export async function suggestMentors(studentId: string): Promise<ApiResult<readonly string[]>> {
  const raw = await request("/api/enterprise/match/suggest", { method: "POST", body: { studentId } });
  if (raw !== null && raw.status === 200 && isObject(raw.json) && isStringList(raw.json["mentorIds"])) {
    return { ok: true, value: raw.json["mentorIds"] };
  }
  return failure<readonly string[]>(raw);
}

/* ==========================================================================
   Sub-cohorts (Sep 17 2026): the institution's own groups of its enrolled
   students. Admin and owner only, like Enrollment; a student is named by
   the platform id on the roster and never by name.
   ========================================================================== */

const isCohorts = (json: unknown): json is CohortsPayload =>
  isObject(json) && typeof json["available"] === "boolean" && Array.isArray(json["cohorts"]) && isObject(json["memberships"]);

export const getCohorts = () => get("/api/enterprise/cohorts", isCohorts);

const isCohortRecord = (json: unknown): json is { cohort: CohortRecord } =>
  isObject(json) && isObject(json["cohort"]) && typeof json["cohort"]["id"] === "string";

export async function createCohort(name: string, kind: CohortKind): Promise<ApiResult<CohortRecord>> {
  const raw = await request("/api/enterprise/cohorts", { method: "POST", body: { name, kind } });
  if (raw !== null && raw.status === 201 && isCohortRecord(raw.json)) return { ok: true, value: raw.json.cohort };
  return failure<CohortRecord>(raw);
}

/** Rename, archive or restore. The Worker refuses a duplicate name and a 26th active cohort in its own words. */
export async function updateCohort(
  id: string,
  patch: { name?: string; archived?: boolean },
): Promise<ApiResult<CohortRecord>> {
  const raw = await request(`/api/enterprise/cohorts/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
  if (raw !== null && raw.status === 200 && isCohortRecord(raw.json)) return { ok: true, value: raw.json.cohort };
  return failure<CohortRecord>(raw);
}

/** Put a student in a cohort, out of any other. */
export async function assignCohort(cohortId: string, studentId: string): Promise<ApiResult> {
  const raw = await request(`/api/enterprise/cohorts/${encodeURIComponent(cohortId)}/members`, {
    method: "POST",
    body: { studentId },
  });
  if (raw !== null && raw.status === 200) return { ok: true, value: undefined };
  return failure(raw);
}

/** Take a student out of a cohort. */
export async function clearCohort(cohortId: string, studentId: string): Promise<ApiResult> {
  const raw = await request(
    `/api/enterprise/cohorts/${encodeURIComponent(cohortId)}/members/${encodeURIComponent(studentId)}`,
    { method: "DELETE" },
  );
  if (raw !== null && raw.status === 204) return { ok: true, value: undefined };
  return failure(raw);
}

export interface RefreshOutcome {
  readonly capturedAt: number;
  readonly claimed: Cell<number>;
  /** The Worker's sentence when only part of the run landed (Sep 19 2026):
   *  the headline figures were written and what it names was not. */
  readonly notice?: string;
}

export async function refreshSnapshot(): Promise<ApiResult<RefreshOutcome>> {
  const raw = await request("/api/enterprise/snapshot/refresh", { method: "POST" });
  if (raw !== null && raw.status === 200 && isObject(raw.json) && typeof raw.json["capturedAt"] === "number") {
    return { ok: true, value: raw.json as unknown as RefreshOutcome };
  }
  return failure<RefreshOutcome>(raw);
}

/* ==========================================================================
   The two files the Worker builds itself (Sep 19 2026)

   The full export (CSV files and a README on methods, zipped) and the audit
   trail. Both routes existed with no button, while /pricing, /security and
   the Addendum said an institution could download them. Fetched rather than
   linked, so a refusal (503 before the first figures, 401 after a session
   ends) stays on the page in the Worker's own words instead of replacing the
   page with JSON.
   ========================================================================== */

export type ServerFile = "export" | "audit";

const SERVER_FILES: Readonly<Record<ServerFile, { readonly path: string; readonly fallbackName: string }>> = {
  export: { path: "/api/enterprise/export/csv", fallbackName: "p3-export.zip" },
  audit: { path: "/api/enterprise/export/audit.csv", fallbackName: "p3-audit.csv" },
};

/** The name the Worker gave the file in `content-disposition`, if any. */
export function fileNameOf(disposition: string | null): string | null {
  const match = /filename="([^"]+)"/.exec(disposition ?? "");
  return match?.[1] ?? null;
}

export async function downloadServerFile(file: ServerFile): Promise<ApiResult> {
  if (typeof fetch === "undefined") return { ok: false, status: null, error: NETWORK_ERROR };
  const { path, fallbackName } = SERVER_FILES[file];
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(path, { credentials: "same-origin", signal: controller.signal });
    if (!response.ok) {
      let json: unknown = null;
      try {
        json = await response.json();
      } catch {
        json = null;
      }
      return { ok: false, status: response.status, error: errorOf(json) };
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileNameOf(response.headers.get("content-disposition")) ?? fallbackName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Not in the same task: Safari cancels a download whose address is revoked at once.
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
    return { ok: true, value: undefined };
  } catch {
    return { ok: false, status: null, error: NETWORK_ERROR };
  } finally {
    clearTimeout(timer);
  }
}

/* ==========================================================================
   The join code, and the institution switch (Sep 10 2026)
   ========================================================================== */

export interface RotatedCode {
  readonly code: string;
  readonly url: string;
  /** When the previous code stops resolving; null when there was none. */
  readonly retiresAt: number | null;
}

/** `POST /api/enterprise/cohort/rotate-code`: owners and administrators. */
export async function rotateJoinCode(): Promise<ApiResult<RotatedCode>> {
  const raw = await request("/api/enterprise/cohort/rotate-code", { method: "POST", body: {} });
  if (raw !== null && raw.status === 201 && isObject(raw.json) && typeof raw.json["code"] === "string") {
    return { ok: true, value: raw.json as unknown as RotatedCode };
  }
  return failure<RotatedCode>(raw);
}

/**
 * `POST /api/enterprise/auth/switch`: a new session on another institution
 * the same person administers. The Worker rotates the cookie; the page
 * reloads so every screen reads the new session.
 */
export async function switchInstitution(membershipId: string): Promise<ApiResult<{ orgName: string }>> {
  const raw = await request("/api/enterprise/auth/switch", { method: "POST", body: { membershipId } });
  if (raw !== null && raw.status === 200 && isObject(raw.json) && typeof raw.json["orgName"] === "string") {
    return { ok: true, value: { orgName: raw.json["orgName"] } };
  }
  return failure<{ orgName: string }>(raw);
}

/* ==========================================================================
   The team
   ========================================================================== */

export type MemberRole = "owner" | "admin" | "viewer";

export interface MemberRow {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  readonly title: string | null;
  readonly role: MemberRole;
  readonly status: "invited" | "active";
  readonly last_seen_at: number | null;
}

export interface InviteRow {
  readonly id: string;
  readonly email: string;
  readonly role: MemberRole;
  readonly expires_at: number;
  readonly created_at: number;
}

export interface MembersPayload {
  readonly members: readonly MemberRow[];
  readonly invites: readonly InviteRow[];
}

const isMembers = (json: unknown): json is MembersPayload =>
  isObject(json) && Array.isArray(json["members"]) && Array.isArray(json["invites"]);

export const getMembers = () => get("/api/enterprise/members", isMembers);

export async function inviteMember(
  email: string,
  role: MemberRole,
  name?: string,
): Promise<ApiResult<{ inviteId: string; memberId: string }>> {
  const raw = await request("/api/enterprise/members", {
    method: "POST",
    body: name ? { email, role, name } : { email, role },
  });
  if (raw !== null && raw.status === 201 && isObject(raw.json) && typeof raw.json["memberId"] === "string") {
    return { ok: true, value: raw.json as unknown as { inviteId: string; memberId: string } };
  }
  return failure(raw);
}

export async function changeMemberRole(id: string, role: MemberRole): Promise<ApiResult<{ role: MemberRole }>> {
  const raw = await request(`/api/enterprise/members/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { role },
  });
  if (raw !== null && raw.status === 200 && isObject(raw.json) && typeof raw.json["role"] === "string") {
    return { ok: true, value: raw.json as unknown as { role: MemberRole } };
  }
  return failure(raw);
}

export async function removeMember(id: string): Promise<ApiResult> {
  const raw = await request(`/api/enterprise/members/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (raw !== null && raw.status === 204) return { ok: true, value: undefined };
  return failure(raw);
}
